// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConvexQueries
 * @package @geenius/analytics/convex
 * @description Query collection for packaged analytics read models, including
 * event streams, top-page aggregates, realtime snapshots, cohort analysis, and
 * session replay materialization.
 */

import { v } from "convex/values";
import { query } from "./_generated/server.js";
import {
  analyticsEventSchema,
  cohortAnalysisSchema,
  deviceBreakdownSchema,
  funnelResultSchema,
  pageViewSchema,
  realtimeStatsSchema,
  sessionReplaySnapshotSchema,
  topPageSchema,
} from "./schema.js";

const DAY_MS = 24 * 60 * 60 * 1000;

type CohortRetentionPoint = {
  day: number;
  retainedUsers: number;
  retentionRate: number;
};

type CohortBucket = {
  cohort: string;
  size: number;
  retained: CohortRetentionPoint[];
  lastActivity: number;
};

type CohortAnalysisResult = {
  name: string;
  cohortType: "user" | "session";
  intervals: number[];
  cohorts: CohortBucket[];
  generatedAt: number;
};

function toJsonRecord(
  value: Record<string, unknown> | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!value) return undefined;

  const record: Record<string, string | number | boolean | null> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (
      entry === null ||
      typeof entry === "string" ||
      typeof entry === "number" ||
      typeof entry === "boolean"
    ) {
      record[key] = entry;
    }
  }

  return record;
}

function dayKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function getIdentity(
  event: { userId?: string; sessionId: string },
  cohortType: "user" | "session",
): string {
  return cohortType === "user"
    ? (event.userId ?? event.sessionId)
    : event.sessionId;
}

function buildCohortAnalysis(
  events: Array<{ userId?: string; sessionId: string; timestamp: number }>,
  name: string,
  cohortType: "user" | "session",
  intervals: number[],
): CohortAnalysisResult {
  const sortedIntervals = [
    ...new Set(intervals.filter((day) => day >= 0)),
  ].sort((a, b) => a - b);
  const identities = new Map<
    string,
    Array<{ userId?: string; sessionId: string; timestamp: number }>
  >();

  for (const event of events) {
    const identity = getIdentity(event, cohortType);
    const existing = identities.get(identity) ?? [];
    existing.push(event);
    identities.set(identity, existing);
  }

  const cohorts = [...identities.entries()].reduce(
    (map, [identity, identityEvents]) => {
      const first = [...identityEvents].sort(
        (a, b) => a.timestamp - b.timestamp,
      )[0];
      if (!first) return map;
      const cohort = dayKey(first.timestamp);
      const bucket =
        map.get(cohort) ??
        new Map<
          string,
          Array<{ userId?: string; sessionId: string; timestamp: number }>
        >();
      bucket.set(
        identity,
        [...identityEvents].sort((a, b) => a.timestamp - b.timestamp),
      );
      map.set(cohort, bucket);
      return map;
    },
    new Map<
      string,
      Map<
        string,
        Array<{ userId?: string; sessionId: string; timestamp: number }>
      >
    >(),
  );

  return {
    name,
    cohortType,
    intervals: sortedIntervals,
    cohorts: [...cohorts.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([cohort, members]) => {
        const memberLists = [...members.values()];
        const retained = sortedIntervals.map((day) => {
          const retainedUsers = memberLists.filter((list) => {
            const firstEvent = list[0];
            return firstEvent
              ? list.some(
                  (event) =>
                    Math.floor(
                      (event.timestamp - firstEvent.timestamp) / DAY_MS,
                    ) === day,
                )
              : false;
          }).length;
          const size = memberLists.length;
          return {
            day,
            retainedUsers,
            retentionRate: size > 0 ? (retainedUsers / size) * 100 : 0,
          };
        });

        return {
          cohort,
          size: memberLists.length,
          retained,
          lastActivity:
            memberLists
              .flat()
              .map((event) => event.timestamp)
              .sort((a, b) => b - a)[0] ?? Date.now(),
        };
      }),
    generatedAt: Date.now(),
  };
}

function buildSessionReplay(
  events: Array<{
    sessionId: string;
    timestamp: number;
    url: string;
    userId?: string;
    label: string;
    kind: "event" | "page_view";
    properties?: Record<string, unknown>;
  }>,
  sessionId: string,
) {
  const frames = events
    .filter((event) => event.sessionId === sessionId)
    .sort((left, right) => left.timestamp - right.timestamp)
    .map((event, index, list) => ({
      id: `${event.kind}_${sessionId}_${event.timestamp}_${index}`,
      kind: event.kind,
      label: event.label,
      url: event.url,
      timestamp: event.timestamp,
      deltaMs: index === 0 ? 0 : event.timestamp - list[index - 1]!.timestamp,
      sessionId,
      userId: event.userId,
      properties: toJsonRecord(event.properties),
    }));

  const timestamps = frames.map((frame) => frame.timestamp);
  return {
    sessionId,
    startedAt: timestamps[0] ?? Date.now(),
    endedAt: timestamps[timestamps.length - 1] ?? Date.now(),
    duration:
      timestamps.length > 1
        ? timestamps[timestamps.length - 1]! - timestamps[0]!
        : 0,
    frames,
    activePages: [...new Set(frames.map((frame) => frame.url).filter(Boolean))],
    userId: frames.find((frame) => frame.userId)?.userId,
  };
}

/** Query that returns normalized analytics events filtered by time range and optional user identity. */
export const getEvents = query({
  args: {
    startTime: v.optional(v.number()),
    endTime: v.optional(v.number()),
    userId: v.optional(v.string()),
    limit: v.optional(v.number()),
  },
  returns: v.array(analyticsEventSchema),
  handler: async (ctx, args) => {
    const q = ctx.db.query("analytics_events").order("desc");
    const results = await q.take(args.limit ?? 100);
    return results
      .filter((e) => {
        if (args.startTime && e.timestamp < args.startTime) return false;
        if (args.endTime && e.timestamp > args.endTime) return false;
        if (args.userId && e.userId !== args.userId) return false;
        return true;
      })
      .map(
        ({
          id,
          sessionId,
          name,
          type,
          properties,
          url,
          referrer,
          userId,
          traits,
          device,
          timestamp,
        }) => ({
          id,
          sessionId,
          name,
          type,
          properties,
          url,
          referrer,
          userId,
          traits,
          device,
          timestamp,
        }),
      );
  },
});

/** Query that returns normalized page-view records filtered by time range. */
export const getPageViews = query({
  args: {
    startTime: v.optional(v.number()),
    endTime: v.optional(v.number()),
    limit: v.optional(v.number()),
  },
  returns: v.array(pageViewSchema),
  handler: async (ctx, args) => {
    const results = await ctx.db
      .query("analytics_pageviews")
      .order("desc")
      .take(args.limit ?? 200);
    return results
      .filter((e) => {
        if (args.startTime && e.timestamp < args.startTime) return false;
        if (args.endTime && e.timestamp > args.endTime) return false;
        return true;
      })
      .map(
        ({
          url,
          title,
          referrer,
          properties,
          duration,
          sessionId,
          userId,
          timestamp,
        }) => ({
          url,
          title,
          referrer,
          properties,
          duration,
          sessionId,
          userId,
          timestamp,
        }),
      );
  },
});

/** Query that aggregates the most-viewed pages from recent page-view rows. */
export const getTopPages = query({
  args: { limit: v.optional(v.number()) },
  returns: v.array(topPageSchema),
  handler: async (ctx, { limit }) => {
    const pvs = await ctx.db
      .query("analytics_pageviews")
      .order("desc")
      .take(1000);
    const counts: Record<string, number> = {};
    pvs.forEach((p) => {
      counts[p.url] = (counts[p.url] ?? 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit ?? 10)
      .map(([url, views]) => ({ url, views }));
  },
});

/** Query that aggregates device usage across recent analytics events. */
export const getDeviceBreakdown = query({
  args: {},
  returns: deviceBreakdownSchema,
  handler: async (ctx) => {
    const events = await ctx.db
      .query("analytics_events")
      .order("desc")
      .take(1000);
    const breakdown = { desktop: 0, tablet: 0, mobile: 0 };
    events.forEach((event) => {
      switch (event.device?.device) {
        case "desktop":
          breakdown.desktop++;
          break;
        case "tablet":
          breakdown.tablet++;
          break;
        case "mobile":
          breakdown.mobile++;
          break;
        default:
          break;
      }
    });
    return breakdown;
  },
});

/** Query that returns stored funnel-analysis records, optionally filtered by name. */
export const getFunnelResults = query({
  args: { name: v.optional(v.string()) },
  returns: v.array(funnelResultSchema),
  handler: async (ctx, { name }) => {
    const rows = name
      ? await ctx.db
          .query("analytics_funnels")
          .withIndex("by_name", (q) => q.eq("name", name))
          .collect()
      : await ctx.db.query("analytics_funnels").order("desc").take(20);
    return rows.map(
      ({
        name,
        results,
        totalEntries,
        totalConversions,
        overallConversionRate,
        analyzedAt,
      }) => ({
        name,
        steps: results,
        totalEntries,
        totalConversions,
        overallConversionRate,
        analyzedAt,
      }),
    );
  },
});

/** Query that derives a realtime analytics snapshot from recently tracked events. */
export const getRealtimeStats = query({
  args: {},
  returns: realtimeStatsSchema,
  handler: async (ctx) => {
    const now = Date.now();
    const windowMs = 5 * 60 * 1000;
    const fiveMinAgo = now - windowMs;
    const recent = await ctx.db
      .query("analytics_events")
      .order("desc")
      .take(500);
    const recentPageViews = await ctx.db
      .query("analytics_pageviews")
      .order("desc")
      .take(500);
    const active = recent.filter((e) => e.timestamp >= fiveMinAgo);
    const activePageViews = recentPageViews.filter(
      (pageView) => pageView.timestamp >= fiveMinAgo,
    );
    const visitors = new Set<string>();
    const sessions = new Set<string>();
    const pageVisitors = new Map<string, Set<string>>();
    const deviceBreakdown = { desktop: 0, tablet: 0, mobile: 0 };

    const trackVisitor = (record: {
      url: string;
      sessionId: string;
      userId?: string;
    }) => {
      const visitorId = record.userId ?? record.sessionId;
      visitors.add(visitorId);
      sessions.add(record.sessionId);
      const pageSet = pageVisitors.get(record.url) ?? new Set<string>();
      pageSet.add(visitorId);
      pageVisitors.set(record.url, pageSet);
    };

    activePageViews.forEach(trackVisitor);
    active.forEach((event) => {
      trackVisitor(event);
      switch (event.device.device) {
        case "desktop":
          deviceBreakdown.desktop++;
          break;
        case "tablet":
          deviceBreakdown.tablet++;
          break;
        case "mobile":
          deviceBreakdown.mobile++;
          break;
        default:
          break;
      }
    });
    const conversions = active.filter((event) =>
      event.name.toLowerCase().includes("conversion"),
    ).length;
    return {
      activeVisitors: visitors.size,
      currentPageViews: activePageViews.length,
      activeSessions: sessions.size,
      eventsPerSecond: Number((active.length / (windowMs / 1000)).toFixed(2)),
      conversions,
      conversionRate:
        active.length > 0 ? (conversions / active.length) * 100 : 0,
      deviceBreakdown,
      topActivePages: [...pageVisitors.entries()]
        .sort((a, b) => b[1].size - a[1].size)
        .slice(0, 5)
        .map(([url, pageVisitorSet]) => ({
          url,
          visitors: pageVisitorSet.size,
        })),
      lastUpdated: now,
    };
  },
});

/** Query that returns stored or on-demand generated cohort analysis data. */
export const getCohortAnalysis = query({
  args: {
    name: v.optional(v.string()),
    cohortType: v.optional(v.union(v.literal("user"), v.literal("session"))),
    intervals: v.optional(v.array(v.number())),
  },
  returns: cohortAnalysisSchema,
  handler: async (ctx, args) => {
    const storedName = args.name;

    if (storedName) {
      const stored = await ctx.db
        .query("analytics_cohorts")
        .withIndex("by_name", (q) => q.eq("name", storedName))
        .order("desc")
        .take(1);
      if (stored[0]) {
        const row = stored[0];
        const cohortType: "user" | "session" =
          row.cohortType === "session" ? "session" : "user";
        return {
          name: row.name,
          cohortType,
          intervals: row.intervals,
          cohorts: row.cohorts,
          generatedAt: row.generatedAt,
        };
      }
    }

    const events = await ctx.db
      .query("analytics_events")
      .order("desc")
      .take(1000);
    const pageViews = await ctx.db
      .query("analytics_pageviews")
      .order("desc")
      .take(1000);
    const merged = [
      ...events.map((event) => ({
        userId: event.userId,
        sessionId: event.sessionId,
        timestamp: event.timestamp,
      })),
      ...pageViews.map((pageView) => ({
        userId: pageView.userId,
        sessionId: pageView.sessionId,
        timestamp: pageView.timestamp,
      })),
    ];

    const cohortType: "user" | "session" = args.cohortType ?? "user";
    return buildCohortAnalysis(
      merged,
      args.name ?? "Cohort Analysis",
      cohortType,
      args.intervals ?? [1, 7, 30],
    );
  },
});

/** Query that returns a stored or on-demand generated session-replay snapshot. */
export const getSessionReplay = query({
  args: { sessionId: v.string() },
  returns: sessionReplaySnapshotSchema,
  handler: async (ctx, { sessionId }) => {
    const stored = await ctx.db
      .query("analytics_session_replays")
      .withIndex("by_sessionId", (q) => q.eq("sessionId", sessionId))
      .order("desc")
      .take(1);
    if (stored[0]) {
      const row = stored[0];
      return {
        sessionId: row.sessionId,
        startedAt: row.startedAt,
        endedAt: row.endedAt,
        duration: row.duration,
        frames: row.frames,
        activePages: row.activePages,
        userId: row.userId,
      };
    }

    const events = await ctx.db
      .query("analytics_events")
      .withIndex("by_sessionId", (q) => q.eq("sessionId", sessionId))
      .collect();
    const pageViews = await ctx.db
      .query("analytics_pageviews")
      .withIndex("by_sessionId", (q) => q.eq("sessionId", sessionId))
      .collect();
    const replayEvents: Array<{
      sessionId: string;
      timestamp: number;
      url: string;
      userId?: string;
      label: string;
      kind: "event" | "page_view";
      properties?: Record<string, unknown>;
    }> = [];

    for (const event of events) {
      replayEvents.push({
        sessionId: event.sessionId,
        timestamp: event.timestamp,
        url: event.url,
        userId: event.userId,
        label: event.name,
        kind: "event",
        properties: event.properties ?? undefined,
      });
    }

    for (const pageView of pageViews) {
      replayEvents.push({
        sessionId: pageView.sessionId,
        timestamp: pageView.timestamp,
        url: pageView.url,
        userId: pageView.userId,
        label: pageView.title ?? pageView.url,
        kind: "page_view",
        properties:
          pageView.duration !== undefined
            ? { duration: pageView.duration }
            : undefined,
      });
    }

    return buildSessionReplay(replayEvents, sessionId);
  },
});
